import React, { MouseEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import { ErrorOverlay } from '@ff-os/shared-react/components/error-overlay/ErrorOverlay';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { Icon } from '@ff-os/shared-react/components/icons/Icon';
import { Heading } from '@ff-os/shared-react/components/panel/Heading';
import { Panel } from '@ff-os/shared-react/components/panel/Panel';
import { selectAccessorySizeById } from '@ff-scene-plugins/photo-prints-redux/selectors/sizeSelectors';
import {
    selectHasErrorByGroupId,
    selectPhotoIdsByGroupId,
    hasValidAccessoryQuantityByGroupId,
    getAccessoryValidationByGroupId
} from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import classNames from 'classnames';
import { selectActiveBorderOptionForVariantId } from '../../../_redux-borders';
import { selectAmountInQueueByGroupId, selectActiveVariant } from '../../../_redux-groups';
import { selectActivePaperOptionForVariantId } from '../../../_redux-papers';
import { selectActiveSizeOptionForVariantId } from '../../../_redux-sizes';
import { setFrameId, selectFrameId, selectAccessoryId, setAccessoryId } from '../../../_redux-ui';
import { applyAccessoryThunk } from '../../../thunks/applyAccessoryThunk';
import { applyMagicBorderThunk } from '../../../thunks/applyMagicBorderThunk';
import { VariantOption } from '../../../VariantOption';
import { FotoFunMobileApplyButton } from '../../fotofun/components/FotoFunMobileApplyButton';
import { MultipleAccessoriesWindow } from '../../photo-print-accessories/MultipleAccessoriesWindow';
import { useGroupTitle } from '../helpers/useGroupTitle';
import { TruncatedPhotoPrintsList } from './TruncatedPhotoPrintsList';
import styles from './PhotoPrintsGroupPreview.module.scss';

const _NotEmptyPhotoPrintsGroupPreview = ({
    setDetailWindowOpen, setDetailFotofunWindowOpen, photoIds, size, paper, border, className,
    failedUploadsCount, successfulUploadsCount, activeVariant,
    amountInQueue, hasError, browse, groupId, applyMagicBorder,
    clearFramesAndAccessory, applyAccessory,
    frameIdMobile, accessoryIdMobile,
    hasValidAcccessoryQuantities, getAccessoryValidation
}: Props) => {

    const { t } = useTranslation();

    const [isAccessoriesOpen, setIsAccessoriesOpen] = useState(false);
    const [activeAccessory, setActiveAccessory] = useState("");

    // Get the selected accessory details
    const selectedAccessory = useSelector((state: PhotoPrintsPdpState) =>
        accessoryIdMobile ? selectAccessorySizeById(state, accessoryIdMobile) : null
    );
    const accessoryMinimumQuantity = selectedAccessory?.minimumQuantity ?? 1;

    const showMagicButtons = Boolean(frameIdMobile || accessoryIdMobile);
    const title = useGroupTitle({ groupId, size, paper, border });
    const accessoryValidation = getAccessoryValidation ?? null;

    const addMore: MouseEventHandler = (event) => {
        event.stopPropagation();
        browse();
    };

    const openAccessories = (accessoryId: string) => {
        setActiveAccessory(accessoryId);
        setIsAccessoriesOpen(true);
    };

    const closeAccessories = () => {
        setActiveAccessory("");
        setIsAccessoriesOpen(false);
    };

    const applyFrames = () => {
        applyMagicBorder(frameIdMobile);
        clearFramesAndAccessory();
    };

    const handleApplyAccessory = () => {
        // Since accessories are now just simple sizes, apply them directly
        applyAccessory(accessoryIdMobile, activeVariant.borderId);
        clearFramesAndAccessory();
    };

    const handleApplyClick: MouseEventHandler = (event) => {

        event.stopPropagation();

        if (accessoryIdMobile) {
            if (accessoryMinimumQuantity > photoIds.length) {
                handleApplyAccessory();
            } else {
                openAccessories(accessoryIdMobile);
            }
        }

        if (frameIdMobile) {
            applyFrames();
            setDetailFotofunWindowOpen(true);
        }
    };

    const panelClassName = classNames(className, styles.container, {
        [styles.errorBorder]: !hasValidAcccessoryQuantities
    });

    const handlePanelClick = () => {
        if (accessoryIdMobile) {
            if (accessoryMinimumQuantity > photoIds.length) {
                handleApplyAccessory();
            } else {
                openAccessories(accessoryIdMobile);
            }
        } else {
            setDetailWindowOpen(true);
        }
    };

    return (
        <Panel className={panelClassName} onClick={handlePanelClick}>
            <Heading icon='shopping-cart' className={styles.heading}>
                {title}
            </Heading>

            {!hasValidAcccessoryQuantities && accessoryValidation && (
                <p className={styles.quantityErrorMessage}>
                    <Icon type="alert-circle-info" strokeWidth="1.7" />
                    {t('photo_prints.accessory_quantity_errored', {
                        minimum: accessoryValidation.minimumRequired,
                        shortage: accessoryValidation.shortage})}
                </p>
            )}

            <div className={styles.listContainer}>

                {hasError && (
                    <ErrorOverlay />
                )}

                <TruncatedPhotoPrintsList
                    photoIds={photoIds}
                    amountInQueue={amountInQueue}
                    maxPreviewCount={2} />

            </div>

            <div className={`${styles.buttonView} ${showMagicButtons ? styles.activeMagic : ""}`}>
                <div className={styles.normalButtons}>
                    <Grid gap='xs' wrap>
                        <Cell className={styles.buttonCell}>
                            <div className={classNames(styles.button, styles.addMoreButton)}
                                onClick={addMore}>
                                <Icon size='small' type='image-file-upload' className={styles.icon} />
                                {t('photo_prints.add_more')}
                            </div>
                        </Cell>

                        <Cell className={styles.buttonCell}>
                            <div className={classNames(styles.button, styles.editButton)}>
                                {t('commons.edit')}
                            </div>
                        </Cell>
                    </Grid>
                </div>
                <div className={styles.applyMagicButtons}>
                    <Grid gap='xs' wrap>
                        {(!accessoryIdMobile || accessoryMinimumQuantity === 1) && (
                            <Cell>
                                <button className={styles.FotofunPreviewButton}>
                                    <span className={styles.text}>
                                        {accessoryIdMobile && accessoryMinimumQuantity === 1
                                            ? t('photo_prints.choose_from_group')
                                            : t('photo_prints.show_more')}
                                    </span>
                                </button>
                            </Cell>
                        )}
                        {(!accessoryIdMobile || accessoryMinimumQuantity > 1) && (
                            <Cell>
                                <FotoFunMobileApplyButton onClick={handleApplyClick} />
                            </Cell>
                        )}
                    </Grid>
                </div>
            </div>

            <div className={styles.message}>
                {failedUploadsCount > 0 && (
                    <>{t('photo_prints.photos_errored', { count: failedUploadsCount })} </>
                )}

                {successfulUploadsCount > 0 && (
                    <>{t('photo_prints.photos_uploaded', { count: successfulUploadsCount })}</>
                )}
            </div>

            {isAccessoriesOpen && (
                <MultipleAccessoriesWindow
                    isOpen={isAccessoriesOpen}
                    activeAccessory={activeAccessory}
                    groupId={groupId}
                    close={closeAccessories} />
            )}

        </Panel>
    );
};

type OwnProps = {
    browse: () => void;
    groupId: string;
    setDetailWindowOpen: (open: boolean) => void;
    setDetailFotofunWindowOpen: (open: boolean) => void;
    className?: string;
    successfulUploadsCount: number;
    failedUploadsCount: number;
    variantId: string;
};

type Props = OwnProps & {
    paper: VariantOption;
    size: VariantOption;
    border?: VariantOption;
    photoIds: string[];
    amountInQueue: number;
    hasError: boolean;
    activeVariant: PhotoPrintsJson.Variant;
    applyMagicBorder: (frameId: string) => void;
    applyAccessory: (accessoryId: string, borderId?: string) => void;
    clearFramesAndAccessory: () => void;
    frameIdMobile: string;
    accessoryIdMobile: string;
    hasValidAcccessoryQuantities?: boolean;
    getAccessoryValidation?: { minimumRequired: number; shortage: number; isValid: boolean };
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    photoIds: selectPhotoIdsByGroupId(state, ownProps.groupId)!,
    amountInQueue: selectAmountInQueueByGroupId(state, ownProps.groupId)!,
    size: selectActiveSizeOptionForVariantId(state, ownProps.variantId)!,
    paper: selectActivePaperOptionForVariantId(state, ownProps.variantId)!,
    border: selectActiveBorderOptionForVariantId(state, ownProps.variantId),
    hasError: selectHasErrorByGroupId(state, ownProps.groupId),
    activeVariant: selectActiveVariant(state, ownProps.groupId),
    frameIdMobile: selectFrameId(state) || "",
    accessoryIdMobile: selectAccessoryId(state) || "",
    hasValidAcccessoryQuantities: hasValidAccessoryQuantityByGroupId(state, ownProps.groupId),
    getAccessoryValidation: getAccessoryValidationByGroupId(state, ownProps.groupId)
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    applyMagicBorder: (frameIdMobile: string) => {
        dispatch(applyMagicBorderThunk(ownProps.groupId, frameIdMobile));
    },
    clearFramesAndAccessory: () => {
        dispatch(setFrameId(""));
        dispatch(setAccessoryId(""));
    },
    applyAccessory: (accessoryIdMobile: string, borderId?: string) => {
        dispatch(applyAccessoryThunk({
            groupId: ownProps.groupId,
            accessoryId: accessoryIdMobile,
            borderId: borderId}));
    }
});

const mergeProps = (stateProps: ReturnType<typeof mapStateToProps>, dispatchProps: ReturnType<typeof mapDispatchToProps>, ownProps: OwnProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps
});

export const NotEmptyPhotoPrintsGroupPreview = connect(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(_NotEmptyPhotoPrintsGroupPreview);